import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import {
  BodyL,
  BodyS,
  BrandXL,
  DisplayXL,
  LabelL,
} from '@sorare/core/src/atoms/typography';
import { FrameCorners } from '@sorare/core/src/atoms/ui/FrameCorners';
import { SecondaryPageBackground } from '@sorare/core/src/atoms/ui/SecondaryPageBackground';
import { TimelineRow } from '@sorare/core/src/components/TimelineRow';
import { TermsDialog } from '@sorare/core/src/components/conversionCredit/TermsDialog';
import { Dialog } from '@sorare/core/src/components/dialog';
import { Header } from '@sorare/core/src/components/dialog/Header';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import { SocialShare } from '@sorare/core/src/components/user/SocialShare';
import { useIsMobile } from '@sorare/core/src/hooks/device/useIsMobile';
import { glossary } from '@sorare/core/src/lib/glossary';

import { RewardsContent } from './RewardsContent/index.tsx';
import type { DuoTaskHowItWorksDialog_taskCampaign } from './__generated__/index.graphql.ts';

export const DuoTaskHowItWorksDialog = ({
  campain,
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
  campain: DuoTaskHowItWorksDialog_taskCampaign;
}) => {
  const isMobile = useIsMobile();
  const [openTerms, setOpenTerms] = useState(false);
  const [tab, setTab] = useState<'invite' | 'rewards'>('invite');
  const {
    shareUrl,
    description,
    detailSections,
    headerMobileUrl,
    headerUrl,
    shareMessage,
    steps,
  } = campain;
  const bestReward = steps.at(-1)?.rewardConfigs.at(0);

  return (
    <Dialog open={open} onClose={onClose} hideHeader fullWidth maxWidth="xs">
      <Vertical gap={0} className="relative min-h-full">
        <SecondaryPageBackground
          overlayStartColor="var(--c-button-secondary-surface)"
          overlayMidColor="var(--c-competition-challenger-primary)"
          overlayEndColor="var(--c-competition-mls-tertiary)"
        />
        <div className="z-1 w-full">
          <Header
            onClose={onClose}
            title={
              <TabBar variant="primary" value={tab}>
                <Tab value="invite" onClick={() => setTab('invite')}>
                  <FormattedMessage {...glossary.invite} />
                </Tab>
                <Tab value="rewards" onClick={() => setTab('rewards')}>
                  <FormattedMessage {...glossary.rewards} />
                </Tab>
              </TabBar>
            }
          />
        </div>
        {/* pb-9 reserves the pinned footer's height so the last step can scroll clear of it. */}
        <Vertical gap={2} center className="px-2 pt-1.5 pb-9">
          <Vertical center gap={1.5}>
            <div className="relative px-2 py-1">
              <FrameCorners cornerSize={8} className="text-color-contrast" />
              <BrandXL as="span" className="text-color-contrast">
                <FormattedMessage
                  defaultMessage="Duo <display>Board</display>"
                  values={{
                    display: chunks => (
                      <DisplayXL as="span">{chunks}</DisplayXL>
                    ),
                  }}
                />
              </BrandXL>
            </div>
            <BodyL className="text-center" color="var(--c-black)">
              {bestReward ? (
                <FormattedMessage
                  defaultMessage="Share your link and <b>win up to {amount}</b> and more per invite!"
                  values={{
                    amount: <RewardConfigText anyRewardConfig={bestReward} />,
                    b: chunks => (
                      <BodyL as="span" bold>
                        {chunks}
                      </BodyL>
                    ),
                  }}
                />
              ) : (
                description
              )}
            </BodyL>
          </Vertical>
          {tab === 'invite' && (
            <>
              <img
                className="h-25 object-contain"
                src={isMobile ? headerMobileUrl : headerUrl}
                alt=""
              />
              <Vertical
                gap={0}
                style={
                  { '--progress-color': 'var(--c-white)' } as CSSProperties
                }
              >
                {detailSections.map((section, i) => (
                  <TimelineRow
                    key={section.title}
                    progress={i === 0 ? 0.5 : 0}
                    isLastRow={i === detailSections.length - 1}
                    icon={
                      <LabelL bold>
                        <FormattedNumber value={i + 1} />
                      </LabelL>
                    }
                    label={
                      <Vertical gap={0.5}>
                        <LabelL bold>{section.title}</LabelL>
                        <BodyS color="var(--c-nd-600)">
                          {section.description}
                        </BodyS>
                      </Vertical>
                    }
                  />
                ))}
              </Vertical>
            </>
          )}
          {tab === 'rewards' && <RewardsContent campain={campain} />}
        </Vertical>
        <Vertical
          gap={0.5}
          center
          className="sticky bottom-2 z-1 mt-auto w-full px-2"
        >
          <SocialShare
            url={shareUrl || ''}
            title="share"
            message={shareMessage || undefined}
            trackingEventName="Share Duo Mission"
            trackingEventContext="Missions Page"
            renderButton={({ onClick }) => (
              <Button size="large" color="primary" fullWidth onClick={onClick}>
                <FormattedMessage defaultMessage="Invite a Duo Partner" />
              </Button>
            )}
          />
          <ButtonBase className="underline" onClick={() => setOpenTerms(true)}>
            <BodyS color="var(--c-nd-600)">
              *
              <FormattedMessage defaultMessage="Referrals terms applied" />
            </BodyS>
          </ButtonBase>
          <TermsDialog
            open={openTerms}
            terms="duoMissionTerms"
            onClose={() => setOpenTerms(false)}
          />
        </Vertical>
      </Vertical>
    </Dialog>
  );
};

DuoTaskHowItWorksDialog.fragments = {
  taskCampaign: gql`
    fragment DuoTaskHowItWorksDialog_taskCampaign on TaskCampaign {
      slug
      description
      shareUrl
      headerMobileUrl: headerUrl(mobile: true)
      headerUrl
      shareMessage
      detailSections {
        title
        description
      }
      ...RewardsContent_taskCampaign
    }
    ${RewardsContent.fragments.taskCampaign}
  ` as TypedDocumentNode<DuoTaskHowItWorksDialog_taskCampaign>,
};
